import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { formatPHP } from "@/lib/format";
import { toast } from "sonner";
import { CreditCard, Truck, Building2, AlertCircle } from "lucide-react";

const METHODS = [
  { key: "bank_transfer", label: "Bank Transfer", icon: Building2, description: "Pay via direct bank deposit / fund transfer. We will send instructions after order confirmation." },
  { key: "cod", label: "Cash on Delivery", icon: Truck, description: "Pay cash on delivery (Metro Manila & select areas)." },
  { key: "manual", label: "Manual / Invoice", icon: CreditCard, description: "Receive a proforma invoice for procurement and PO processing." },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    city: "",
    notes: "",
    payment_method: "bank_transfer",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
      };
      const { data } = await api.post("/orders", payload);
      clear();
      navigate(`/order/${data.reference}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="checkout-empty">
        <div className="text-center">
          <h1 className="font-heading font-extrabold text-3xl mb-3">Your cart is empty</h1>
          <Link to="/shop" className="btn-msr-primary">Browse Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page">
      <section className="bg-white border-b border-msr-line">
        <div className="msr-container py-12">
          <div className="overline mb-3">Checkout</div>
          <h1 className="font-heading font-extrabold text-4xl lg:text-5xl tracking-tight" data-testid="checkout-title">Place your order</h1>
        </div>
      </section>

      <section className="bg-msr-surface">
        <div className="msr-container py-12 grid lg:grid-cols-12 gap-8">
          <form onSubmit={submit} className="lg:col-span-8 space-y-6">
            <div className="msr-card">
              <div className="overline mb-4">Contact information</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" value={form.full_name} onChange={(v) => update("full_name", v)} required testId="checkout-name" />
                <Field label="Email *" type="email" value={form.email} onChange={(v) => update("email", v)} required testId="checkout-email" />
                <Field label="Phone *" value={form.phone} onChange={(v) => update("phone", v)} required testId="checkout-phone" />
                <Field label="Company" value={form.company} onChange={(v) => update("company", v)} testId="checkout-company" />
              </div>
            </div>

            <div className="msr-card">
              <div className="overline mb-4">Delivery address</div>
              <div className="grid grid-cols-1 gap-4">
                <Field label="Address *" value={form.address} onChange={(v) => update("address", v)} required testId="checkout-address" />
                <Field label="City / Province" value={form.city} onChange={(v) => update("city", v)} testId="checkout-city" />
                <TextArea label="Order notes" value={form.notes} onChange={(v) => update("notes", v)} rows={3} testId="checkout-notes" />
              </div>
            </div>

            <div className="msr-card">
              <div className="overline mb-2">Payment method</div>
              <div className="inline-flex items-center gap-2 text-xs bg-msr-blue/5 text-msr-blue px-3 py-1.5 mb-4">
                <AlertCircle size={14} /> Payment gateway coming soon — manual options below.
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {METHODS.map(({ key, label, icon: Icon, description }) => (
                  <label
                    key={key}
                    className={`border p-4 cursor-pointer transition-colors ${form.payment_method === key ? "border-msr-blue bg-msr-blue/5" : "border-msr-line bg-white hover:border-msr-blue/40"}`}
                    data-testid={`checkout-method-${key}`}
                  >
                    <input
                      type="radio"
                      name="method"
                      value={key}
                      checked={form.payment_method === key}
                      onChange={() => update("payment_method", key)}
                      className="sr-only"
                    />
                    <Icon size={18} className="text-msr-blue mb-2" />
                    <div className="font-heading font-bold text-sm">{label}</div>
                    <div className="text-xs text-msr-slate mt-1">{description}</div>
                  </label>
                ))}
              </div>
            </div>

            <button type="submit" disabled={submitting} className="btn-msr-primary w-full disabled:opacity-60" data-testid="checkout-submit-btn">
              {submitting ? "Placing order..." : `Place Order — ${formatPHP(subtotal)}`}
            </button>
          </form>

          <aside className="lg:col-span-4">
            <div className="msr-card border-t-4 border-t-msr-blue sticky top-28" data-testid="checkout-summary">
              <div className="overline mb-4">Order summary</div>
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {items.map((i) => (
                  <div key={i.id} className="flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{i.name}</div>
                      <div className="text-xs text-msr-slate">Qty: {i.quantity}</div>
                    </div>
                    <div className="font-heading font-bold text-msr-ink">{formatPHP(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-msr-line mt-4 pt-4 space-y-2 text-sm">
                <Row label="Subtotal" value={formatPHP(subtotal)} />
                <Row label="Shipping" value="Calculated separately" />
                <div className="flex justify-between pt-3 mt-3 border-t border-msr-line">
                  <span className="font-heading font-extrabold">Total</span>
                  <span className="font-heading font-extrabold text-2xl text-msr-blue">{formatPHP(subtotal)}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full border border-msr-line bg-white px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue"
        data-testid={testId}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wider text-msr-slate">
      {label}
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full border border-msr-line bg-white px-3 py-2.5 text-sm text-msr-ink font-normal normal-case tracking-normal focus:outline-none focus:border-msr-blue resize-none"
        data-testid={testId}
      />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-msr-slate">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
